import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useStyles } from 'uinix-ui';

import { ActionsMenu } from '../../components/ActionsMenu/ActionsMenu';
import { Box } from '../../components/Box/Box';
import { Layout } from '../../components/Layout/Layout';
import { Link } from '../../components/Link/Link';
import { SubNavLink } from '../../types/NavLink';
import { testIsActiveLink } from './utils';

interface Props {
  activePathname: string;
  links: SubNavLink[];
}

const messages = defineMessages({
  subNavigationLabel: {
    id: 'eds.SubNav.subNavigationLabel',
    defaultMessage: 'sub navigation',
    description: 'Accessible label for the sub navigation region',
  },
});

export const SubNav = ({ activePathname, links }: Props) => {
  const styles = useStyles();
  const intl = useIntl();

  return (
    <Box
      as="nav"
      aria-label={intl.formatMessage(messages.subNavigationLabel)}
      bg="background"
      flex="auto"
    >
      <Layout as="ul" variant="unset.list">
        {links.map((link) => {
          const { label, pathname, onClick } = link;
          const isActiveLink = testIsActiveLink(activePathname)(link);
          const { children } = link;

          const isMenu = Boolean(children);

          const content = children ? (
            <ActionsMenu
              icon="chevron-down"
              name={`${label} menu`}
              text={label}
              tooltip=""
              actions={children.map((childLink) => ({
                ...childLink,
                mode: childLink.isEmphasized ? 'link' : undefined,
                value: childLink.pathname,
              }))}
            />
          ) : (
            <Box maxW="navlink.max-width">
              <Link
                shouldTruncate
                color="text.primary"
                isActive={isActiveLink}
                pathname={pathname}
                variant="body-medium"
                onClick={onClick}
              >
                {label}
              </Link>
            </Box>
          );

          return (
            <Layout
              key={pathname}
              align="center"
              as="li"
              flex="none"
              styles={isMenu ? null : styles.tabs.tab}
              styleProps={{ isSelected: isActiveLink }}
            >
              {content}
            </Layout>
          );
        })}
      </Layout>
    </Box>
  );
};
